import React, { useEffect } from 'react';
import { Navigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

const legalContent = {
  datenschutz: {
    title: "Datenschutzerklärung",
    content: `Verantwortliche Stelle: easywebswiss.ch (by bitehub), bitehub.randazzo, Breitestrasse 20, 5015 Erlinsbach SO, Schweiz. UID: CHE-422.954.335. E-Mail: info@easywebswiss.ch

1. Allgemeines
Der Schutz Ihrer personenbezogenen Daten ist uns ein wichtiges Anliegen. Wir halten uns an die EU-DSGVO und das Schweizer Datenschutzgesetz (DSG).

2. Erhobene Daten
Beim Besuch der Website: IP-Adresse, Datum/Uhrzeit, Browsertyp, Betriebssystem, aufgerufene Seiten.
Bei Kontaktaufnahme: Name, E-Mail, Telefon, Nachrichteninhalt.

3. Cookies und Tracking
Notwendige Cookies für den Betrieb, Analyse-Cookies (Google Analytics) und Marketing-Cookies nur mit Ihrer Zustimmung.

4. Ihre Rechte
Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit, Widerruf.

5. Kontakt
Bei Fragen: info@easywebswiss.ch`
  },
  impressum: {
    title: "Impressum",
    content: `easywebswiss.ch (by bitehub)
bitehub.randazzo

Breitenstrasse 20
5015 Erlinsbach SO
Schweiz

UID: CHE-422.954.335

E-Mail: info@easywebswiss.ch

Telefon:
Valentina Randazzo: +41 78 320 24 61
Nicola Violante: +41 78 332 94 40

Verantwortlich für den Inhalt: bitehub.randazzo

Instagram: @easywebswiss.ch`
  },
  agb: {
    title: "Allgemeine Geschäftsbedingungen",
    content: `1. Geltungsbereich
Diese AGB gelten für alle Dienstleistungen von easywebswiss.ch.

2. Leistungen
Webdesign, Webentwicklung, SEO-Optimierung, Online-Marketing, Website-Betreuung.

3. Preise und Zahlung
Preise in CHF, exkl. MwSt. Zahlungsfrist: 30 Tage.

4. Urheberrecht
Bis zur vollständigen Bezahlung verbleiben alle Rechte bei easywebswiss.ch.

5. Gewährleistung
Mängel sind innert 14 Tagen schriftlich zu rügen.

6. Gerichtsstand
Schweizer Recht, Gerichtsstand: Aarau

Kontakt: info@easywebswiss.ch`
  },
  cookies: {
    title: "Cookie-Richtlinie",
    content: `Was sind Cookies?
Cookies sind kleine Textdateien, die auf Ihrem Gerät gespeichert werden.

Welche Cookies verwenden wir?
- Notwendige Cookies: Session, Cookie-Einstellungen
- Analyse-Cookies: Google Analytics (nur mit Zustimmung)
- Marketing-Cookies: Remarketing (nur mit Zustimmung)

Ihre Einstellungen
Sie können Ihre Cookie-Einstellungen jederzeit über den Cookie-Banner ändern.

Kontakt: info@easywebswiss.ch`
  }
};

export default function LegalPage() {
  const location = useLocation();
  const pageKey = location.pathname.replace('/', '');
  
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  const pageData = legalContent[pageKey];

  if (!pageData) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="min-h-screen bg-white pt-24" data-testid={`legal-page-${pageKey}`}>
      <div className="max-w-[800px] mx-auto px-4 md:px-8 py-16 md:py-24">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#666] hover:text-[#0a0a0a] transition-colors mb-12"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </Link>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-5xl font-bold text-[#0a0a0a] font-['Manrope'] mb-12"
        >
          {pageData.title}
        </motion.h1>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="prose prose-lg max-w-none"
        >
          {pageData.content.split('\n\n').map((paragraph, index) => (
            <p key={index} className="text-[#666] leading-relaxed mb-6 whitespace-pre-line">
              {paragraph}
            </p>
          ))}
        </motion.div>

        {/* Last Updated */}
        <div className="mt-16 pt-8 border-t border-[#e5e5e5] text-[#999] text-sm">
          Stand: Februar 2026
        </div>
      </div>
    </main>
  );
}
